import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { BankTransaction } from '@syndiqia/ledger/contracts';
import { useApi } from '../api';
import { useApprovals, useCollectionsStatus } from '../approvals-queries';
import { useMyUnits, useRequests } from '../comms-queries';
import { Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useAllOwnership, useArrears, useBudget, useCalls, useCurrentYear, useDueToFunds, useFiscalYears, useFundSummaries, useFunds, useStatement } from '../ledger-queries';
import { useMeetings } from '../governance-queries';
import type { Booking } from '@syndiqia/reservations/contracts';
import type { InsuranceSummary } from '@syndiqia/insurance/contracts';
import type { WrittenResolution } from '@syndiqia/governance/contracts';
import { useMaintenanceSummary } from '../maintenance-queries';
import { useBuildings, useCurrentQuoteParts, useParties, useUnits } from '../queries';
import { useMe, useSyndicate } from '../session';

/** Landing page of a syndicate: what needs attention, then the state of things. */
export function DashboardPage() {
  const syndicate = useSyndicate();
  return (
    <>
      <PageHeader title={syndicate.name} />
      {syndicate.can('ledger.read') ? <BoardDashboard /> : <MemberDashboard />}
    </>
  );
}

function BoardDashboard() {
  const { t, money, date, locale } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const { year, isPending: yearPending } = useCurrentYear();
  const budget = useBudget(year?.id);
  const calls = useCalls(year?.id);
  const hasLedger = !!year;
  const funds = useFunds();
  const summaries = useFundSummaries(hasLedger);
  const arrears = useArrears(hasLedger);
  const dueToFunds = useDueToFunds(hasLedger);
  const bank = useQuery({
    queryKey: [s.tenantId, 'ledger', 'bank', 'unmatched'],
    queryFn: () => api<BankTransaction[]>(`${s.api}/ledger/bank-transactions?status=unmatched`),
    enabled: hasLedger,
  });
  const units = useUnits();
  const ownership = useAllOwnership();
  const parties = useParties();
  const quoteParts = useCurrentQuoteParts();
  const requests = useRequests('open');
  const approvals = useApprovals('pending');
  const collections = useCollectionsStatus();
  const maintenance = useMaintenanceSummary();
  const meetings = useMeetings();
  const me = useMe();
  const bookingRequests = useQuery({
    queryKey: [s.tenantId, 'reservations', 'bookings', 'all'],
    queryFn: () => api<Booking[]>(`${s.api}/reservations/bookings?scope=all`),
    enabled: s.can('reservations.manage'),
  });
  const mt = maintenance.data;
  const onboarding = useQuery({
    queryKey: [s.tenantId, 'onboarding'],
    queryFn: () => api<{ steps: { step: string; status: string }[] }>(`${s.api}/onboarding`),
    enabled: s.can('onboarding.manage'),
  });

  const today = new Date().toISOString().slice(0, 10);
  const owned = new Set(ownership.data?.map((o) => o.unitId));
  const unitsWithoutOwner = ownership.data ? (units.data ?? []).filter((u) => !owned.has(u.id)).length : 0;
  const callsDue = calls.data?.filter((c) => !c.entryId && c.dueDate <= today).length ?? 0;
  const transferTotal = dueToFunds.data?.reduce((sum, d) => sum + d.amountCents, 0) ?? 0;

  const todo: { key: string; text: string; to: string }[] = [];
  // Onboarding: while a step waits for review, or while the client has no lots yet.
  const onboardingSteps = onboarding.data?.steps ?? [];
  const toReview = onboardingSteps.filter((x) => x.status === 'ready').length;
  const notApplied = onboardingSteps.filter((x) => x.status !== 'applied').length;
  if (toReview > 0) todo.push({ key: 'onboarding-review', text: t.onboarding.toReview(toReview), to: `${s.base}/onboarding` });
  else if (onboarding.data && units.data && units.data.length === 0 && notApplied > 0) todo.push({ key: 'onboarding', text: t.onboarding.toStart, to: `${s.base}/onboarding` });
  for (const a of approvals.data ?? []) {
    if (a.kind === 'governance.notice') {
      const notice = a.payload as unknown as { meetingId: string; meetingTitle: string };
      if (s.can('governance.approve')) todo.push({ key: `approval-${a.id}`, text: t.governance.noticeTodo(notice.meetingTitle), to: `${s.base}/governance/${notice.meetingId}` });
    } else if (s.can('collections.approve')) todo.push({ key: `approval-${a.id}`, text: t.approvals.todo(a.payload.items.length), to: `${s.base}/approvals/${a.id}` });
  }
  const insurance = useQuery({ queryKey: [s.tenantId, 'insurance', 'summary'], queryFn: () => api<InsuranceSummary>(`${s.api}/insurance/summary`), enabled: s.can('insurance.manage') });
  if (insurance.data?.toReview) todo.push({ key: 'ins-review', text: t.insurance.todoReview(insurance.data.toReview), to: `${s.base}/insurance` });
  if (insurance.data && insurance.data.expired + insurance.data.insufficient > 0) todo.push({ key: 'ins-missing', text: t.insurance.todoMissing(insurance.data.expired + insurance.data.insufficient), to: `${s.base}/insurance` });
  const written = useQuery({ queryKey: [s.tenantId, 'governance', 'written'], queryFn: () => api<WrittenResolution[]>(`${s.api}/governance/written`), enabled: s.can('governance.approve') });
  for (const w of written.data ?? []) {
    if (w.status === 'circulating' && w.signatures.some((x) => x.userId === me.data?.user.id && !x.signedAt))
      todo.push({ key: `written-${w.id}`, text: t.governance.signTodo(w.title), to: `${s.base}/governance/written` });
  }
  const toApprove = bookingRequests.data?.filter((b) => b.status === 'requested').length ?? 0;
  if (toApprove > 0) todo.push({ key: 'bookings', text: t.reservations.requestsTodo(toApprove), to: `${s.base}/reservations` });
  for (const m of meetings.data ?? []) {
    const days = Math.ceil((Date.parse(m.scheduledAt) - Date.now()) / 86_400_000);
    if (m.minutesStatus === 'draft' && s.can('governance.approve')) todo.push({ key: `minutes-${m.id}`, text: t.governance.minutesTodo(m.title), to: `${s.base}/governance/${m.id}` });
    else if (m.status === 'draft' && m.kind !== 'board' && days >= 0 && days <= 45 && !approvals.data?.some((a) => a.kind === 'governance.notice'))
      todo.push({ key: `meeting-${m.id}`, text: t.governance.upcomingTodo(m.title, days), to: `${s.base}/governance/${m.id}` });
  }
  for (const c of collections.data?.filter((x) => x.stage === 3) ?? []) {
    todo.push({ key: `escalated-${c.unitId}`, text: t.collections.escalated(units.data?.find((u) => u.id === c.unitId)?.label ?? '…'), to: `${s.base}/units/${c.unitId}` });
  }
  const newRequests = requests.data?.filter((r) => r.status === 'new').length ?? 0;
  if (newRequests > 0) todo.push({ key: 'requests', text: t.requests.newCount(newRequests), to: `${s.base}/requests` });
  if (mt) {
    const base = `${s.base}/maintenance`;
    if (mt.vendorReports > 0) todo.push({ key: 'wo-vendor', text: t.maintenance.vendorReportsTodo(mt.vendorReports), to: base });
    if (mt.invoicesToFile > 0) todo.push({ key: 'invoices-file', text: t.purchasing.toFile(mt.invoicesToFile), to: base });
    if (mt.invoicesToApprove > 0) todo.push({ key: 'invoices', text: t.purchasing.toApprove(mt.invoicesToApprove), to: base });
    if (mt.quotesToCompare > 0) todo.push({ key: 'quotes', text: t.purchasing.toCompare(mt.quotesToCompare), to: base });
    if (mt.urgentWorkOrders > 0) todo.push({ key: 'wo-urgent', text: t.maintenance.urgentTodo(mt.urgentWorkOrders), to: base });
    if (mt.overdueTasks > 0) todo.push({ key: 'tasks-overdue', text: t.maintenance.overdueTodo(mt.overdueTasks), to: `${base}/calendar` });
    if (mt.dueSoonTasks > 0) todo.push({ key: 'tasks-soon', text: t.maintenance.dueSoonTodo(mt.dueSoonTasks), to: `${base}/calendar` });
    if (mt.vendorInsuranceAlerts > 0) todo.push({ key: 'vendors', text: t.maintenance.vendorInsuranceTodo(mt.vendorInsuranceAlerts), to: `${base}/vendors` });
    if (!mt.calendarSetUp) todo.push({ key: 'calendar', text: t.maintenance.calendarEmpty, to: `${base}/calendar` });
  }
  if (!yearPending && !year) todo.push({ key: 'year', text: t.home.noYear, to: `${s.base}/finances` });
  if (year && budget.data === null) todo.push({ key: 'budget', text: t.home.noBudget, to: `${s.base}/finances/budget` });
  if (budget.data?.status === 'draft') todo.push({ key: 'budget', text: t.home.budgetDraft, to: `${s.base}/finances/budget` });
  if (bank.data?.length) todo.push({ key: 'bank', text: t.home.reconcile(bank.data.length), to: `${s.base}/finances/bank` });
  if (callsDue > 0) todo.push({ key: 'calls', text: t.home.callsDue(callsDue), to: `${s.base}/finances` });
  if (transferTotal > 0) todo.push({ key: 'transfer', text: t.home.transferDue(money(transferTotal)), to: `${s.base}/finances` });
  if (quoteParts.data === null) todo.push({ key: 'qp', text: t.home.noQuoteParts, to: `${s.base}/quote-parts` });
  if (unitsWithoutOwner > 0) todo.push({ key: 'owners', text: t.home.unitsWithoutOwner(unitsWithoutOwner), to: `${s.base}/units` });

  const late = arrears.data?.filter((r) => r.balanceCents > 0 && r.balanceCents - r.currentCents > 0) ?? [];
  const overdue = late.reduce((sum, r) => sum + r.balanceCents - r.currentCents, 0);
  const label = (id: string) => units.data?.find((u) => u.id === id)?.label ?? '…';
  const owners = (unitId: string) =>
    ownership.data
      ?.filter((o) => o.unitId === unitId)
      .map((o) => parties.data?.find((p) => p.id === o.partyId)?.displayName)
      .join(', ');

  return (
    <div className="dashboard">
      <Card title={t.home.todo} wide>
        {yearPending ? (
          <Loading label={t.loading} />
        ) : todo.length === 0 ? (
          <p className="muted">{t.home.allDone}</p>
        ) : (
          <ul className="todo">
            {todo.map((item) => (
              <li key={item.key}>
                <Link to={item.to}>{item.text}</Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {hasLedger && (
        <Card title={t.home.arrears} action={<Link to={`${s.base}/finances`}>{t.home.seeAll}</Link>}>
          {late.length === 0 ? (
            <p className="muted">{t.finances.noArrears}</p>
          ) : (
            <>
              <p className="figure">{money(overdue)}</p>
              <p className="muted">{t.home.overdueTotal(money(overdue), late.length)}</p>
              <ul className="compact-list">
                {late.slice(0, 5).map((r) => (
                  <li key={r.unitId}>
                    <Link to={`${s.base}/units/${r.unitId}`}>{label(r.unitId)}</Link>
                    <span className="muted">{owners(r.unitId)}</span>
                    <span className="num">{money(r.balanceCents)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      )}

      {hasLedger && (
        <Card title={t.home.funds}>
          <ul className="compact-list">
            {summaries.data?.map((f) => {
              const fund = funds.data?.find((x) => x.code === f.fundCode);
              return (
                <li key={f.fundCode}>
                  <span>{fund ? (locale === 'fr' ? fund.nameFr : fund.nameEn) : f.fundCode}</span>
                  <span className="num">{money(f.netAssetsCents)}</span>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {mt && (
        <Card title={t.maintenance.dashboardTitle} action={<Link to={`${s.base}/maintenance/calendar`}>{t.home.seeAll}</Link>}>
          <p className="muted">
            <Link to={`${s.base}/maintenance`}>{t.maintenance.openOrders(mt.openWorkOrders)}</Link>
          </p>
          {mt.upcoming.length === 0 ? (
            <p className="muted">{t.maintenance.upToDate}</p>
          ) : (
            <ul className="compact-list">
              {mt.upcoming.map((task) => (
                <li key={task.id}>
                  <span>{task.title}</span>
                  <span className={`task-days task-days-${task.status}`}>{task.daysLeft !== null ? t.maintenance.daysLeft(task.daysLeft) : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <SyndicateCard quotePartsFrom={quoteParts.data?.from} coOwners={parties.data?.length} date={date} />
    </div>
  );
}

function MemberDashboard() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const quoteParts = useCurrentQuoteParts();
  const requests = useRequests('open');
  return (
    <div className="dashboard">
      {s.can('requests.create') && (
        <Card title={t.requests.mine} action={<Link to={`${s.base}/requests`}>{t.requests.new}</Link>}>
          {requests.data?.length === 0 && <p className="muted">{t.requests.empty}</p>}
          <ul className="compact-list">
            {requests.data?.map((r) => (
              <li key={r.id}>
                <Link to={`${s.base}/requests/${r.id}`}>#{r.number}</Link>
                <span>{r.subject}</span>
                <span className={`badge badge-status-${r.status}`}>{t.requests.statuses[r.status]}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {s.can('agents.ask') && (
        <Card title={t.ask.title}>
          <Link to={`${s.base}/documents`}>✦ {t.ask.shortcut}</Link>
        </Card>
      )}
      <MyAccount />
      <SyndicateCard quotePartsFrom={quoteParts.data?.from} date={date} />
    </div>
  );
}

function MyAccount() {
  const { t } = useI18n();
  const myUnits = useMyUnits();
  const units = useUnits();
  if (!myUnits.data) return null;
  return (
    <Card title={t.access.myAccount}>
      {myUnits.data.length === 0 && <p className="muted">{t.access.noUnits}</p>}
      <ul className="compact-list">
        {myUnits.data.map((id) => (
          <MyUnitBalance key={id} unitId={id} label={units.data?.find((u) => u.id === id)?.label ?? '…'} />
        ))}
      </ul>
    </Card>
  );
}

function MyUnitBalance({ unitId, label }: { unitId: string; label: string }) {
  const { money } = useI18n();
  const s = useSyndicate();
  const years = useFiscalYears();
  const statement = useStatement(unitId, !!years.data?.length);
  return (
    <li>
      <Link to={`${s.base}/units/${unitId}`}>{label}</Link>
      <span />
      <span className={`num${(statement.data?.balanceCents ?? 0) > 0 ? ' amount-out' : ''}`}>{statement.data ? money(statement.data.balanceCents) : ''}</span>
    </li>
  );
}

function SyndicateCard({ quotePartsFrom, coOwners, date }: { quotePartsFrom?: string | undefined; coOwners?: number | undefined; date: (d: string) => string }) {
  const { t } = useI18n();
  const s = useSyndicate();
  const buildings = useBuildings();
  const units = useUnits();
  return (
    <Card title={t.home.syndicate}>
      <ul className="facts-list">
        {buildings.data?.map((b) => (
          <li key={b.id}>
            <strong>{b.name}</strong> — {b.streetAddress}, {b.city}
          </li>
        ))}
        {buildings.data && buildings.data.length > 1 && <li>{t.home.buildings(buildings.data.length)}</li>}
        {units.data && (
          <li>
            <Link to={`${s.base}/units`}>{t.home.units(units.data.length)}</Link>
          </li>
        )}
        {coOwners !== undefined && <li>{t.home.coOwners(coOwners)}</li>}
        {quotePartsFrom && (
          <li>
            <Link to={`${s.base}/quote-parts`}>{t.home.quotePartsSince(date(quotePartsFrom))}</Link>
          </li>
        )}
      </ul>
    </Card>
  );
}

function Card({ title, action, wide, children }: { title: string; action?: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <section className={`panel dash-card${wide ? ' dash-wide' : ''}`}>
      <header className="dash-card-header">
        <h2>{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}
